import React from 'react';
import { CheckCircle, XCircle, Download, CreditCard } from 'lucide-react';

const AdminPaymentSummary = ({ 
  booking, 
  totalPaid, 
  depositPaid, 
  fullPaymentPaid, 
  payments,
  onDownloadInvoice, 
  onViewPaymentHistory 
}) => {
  const addons = booking.booking_addons || booking.bookingAddons || [];
  const addonsTotal = addons.reduce((sum, addon) => sum + Number(addon.addon_price || 0), 0);

  const completedPayments = payments.filter(p => p.status === 'completed');

  // Calculate amounts using new payment structure
  const subtotal = parseFloat(booking.subtotal_amount || 0);
  const platformFee = parseFloat(booking.platform_fee || 0);
  const totalAmountDue = subtotal + platformFee; // What client pays
  const vendorPayout = parseFloat(booking.vendor_payout_amount || booking.subtotal_amount || 0);
  
  // Calculate deposit amount from actual payments
  const depositAmountPaid = completedPayments
    .filter(p => p.payment_type === 'deposit')
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  // Check if fully paid
  const amountPaid = parseFloat(booking.amount_paid || 0);
  const isFullyPaid = amountPaid >= totalAmountDue && totalAmountDue > 0;

  // Helper function to safely format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num === null || num === undefined) {
      return '0.00';
    }
    return num.toFixed(2);
  };

  return (
    <div className="bg-white border rounded-lg p-2 sm:p-3">
      <h3 className="text-sm sm:text-base font-bold text-black mb-2 sm:mb-3">Payment Summary</h3>
      <div className="space-y-1.5 sm:space-y-2">
        <div className="flex justify-between items-center py-1 border-b border-gray-300 gap-2">
          <span className="text-xs text-gray-600 font-medium flex-shrink-0">Subtotal:</span>
          <span className="text-xs sm:text-sm font-medium text-black text-right break-words">
            ${formatCurrency(subtotal)}
          </span>
        </div>
        {addonsTotal > 0 && (
          <div className="flex justify-between items-center py-1 border-b border-gray-300 gap-2">
            <span className="text-xs text-gray-600 font-medium flex-shrink-0">Add-ons:</span>
            <span className="text-xs sm:text-sm font-medium text-black text-right">${addonsTotal.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between items-center py-1.5 border-b-2 border-gray-300 gap-2">
          <span className="text-xs sm:text-sm text-black font-bold flex-shrink-0">Service Charge</span>
          <span className="text-sm sm:text-base md:text-lg font-bold text-black text-right">
            ${formatCurrency(platformFee)}
          </span>
        </div>
        <div className="flex justify-between items-center py-1 border-b border-gray-300 gap-2">
          <span className="text-xs text-gray-600 font-medium flex-shrink-0">Total Amount Due:</span>
          <span className="text-xs sm:text-sm font-medium text-black text-right">
            ${formatCurrency(totalAmountDue)}
          </span>
        </div>
        {depositAmountPaid > 0 && !isFullyPaid && (
          <div className="flex justify-between items-center py-1 border-b border-gray-300 gap-2">
            <span className="text-xs text-gray-600 font-medium flex-shrink-0">Deposit Amount:</span>
            <span className="text-xs sm:text-sm font-medium text-black text-right">
              ${formatCurrency(depositAmountPaid)}
            </span>
          </div>
        )}
        <div className="flex justify-between items-center py-1.5 border-b-2 border-gray-300 gap-2">
          <span className="text-xs sm:text-sm text-black font-bold flex-shrink-0">Vendor Payout:</span>
          <span className="text-sm sm:text-base md:text-lg font-bold text-black text-right">
            ${formatCurrency(vendorPayout)}
          </span>
        </div>
        <div className="flex justify-between items-center pt-1 gap-2">
          <span className="text-xs text-gray-600 font-medium flex-shrink-0">Total Paid:</span>
          <span className="text-xs sm:text-sm font-medium text-black text-right">
            ${formatCurrency(totalPaid)}
          </span>
        </div>
        <div className="flex justify-between items-center gap-2">
          <span className="text-xs text-gray-600 font-medium flex-shrink-0">Deposit Status:</span>
          <span className={`text-xs font-medium ${depositPaid > 0 ? 'text-black' : 'text-gray-600'} flex items-center gap-1`}>
            {depositPaid > 0 ? (
              <>
                <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span>Paid</span>
              </>
            ) : (
              <>
                <XCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span>Pending</span>
              </>
            )}
          </span>
        </div>
        {isFullyPaid && (
          <div className="flex justify-between items-center pt-1 border-t border-gray-300 gap-2">
            <span className="text-xs text-gray-600 font-medium flex-shrink-0">Full Payment:</span>
            <span className="text-xs font-medium text-black flex items-center gap-1">
              <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
              <span>Paid</span>
            </span>
          </div>
        )}
      </div>
      
      {/* Payment History Section - Always show for admin */}
      {/* <div className="mt-3 sm:mt-4 pt-3 border-t-2 border-gray-300">
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <h4 className="text-xs sm:text-sm font-bold text-black">Payment History</h4>
          {payments.length > 0 && (
            <button
              onClick={onViewPaymentHistory}
              className="text-xs text-gray-600 hover:text-black transition-colors"
            >
              View All
            </button>
          )}
        </div>
        
        <div className="space-y-2">
          {completedPayments.length > 0 ? (
            <>
              {completedPayments.slice(0, 3).map((payment) => (
                <div
                  key={payment.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-2 sm:p-3"
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-black flex-shrink-0" />
                        <span className="text-xs font-medium text-black">
                          {getPaymentTypeLabel(payment.payment_type)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                        <span className="break-words">{formatDate(payment.created_at)}</span>
                      </div>
                      {payment.payment_reference && (
                        <div className="text-xs text-gray-500 mt-0.5 break-all">
                          Ref: {payment.payment_reference}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm sm:text-base font-bold text-black mb-0.5">
                        {formatAmount(payment.amount)}
                      </div>
                      <div className="text-xs text-gray-600 capitalize">
                        {payment.payment_method || 'Card'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {completedPayments.length > 3 && (
                <button
                  onClick={onViewPaymentHistory}
                  className="w-full text-xs text-gray-600 hover:text-black transition-colors py-1"
                >
                  View {completedPayments.length - 3} more payment{completedPayments.length - 3 > 1 ? 's' : ''}
                </button>
              )}
            </>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-600">
                {payments.length > 0 
                  ? 'No completed payments' 
                  : 'No payments recorded'}
              </p>
            </div>
          )}
        </div>
      </div> */}

      {/* Payment Actions */}
      <div className="mt-3 sm:mt-4 pt-3 border-t-2 border-gray-300 space-y-2">
        <button
          onClick={onDownloadInvoice}
          className="flex items-center justify-center gap-1.5 sm:gap-2 w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-black text-black rounded-lg hover:bg-gray-100 transition-colors text-xs sm:text-sm font-medium"
        >
          <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="truncate">Download Invoice</span>
        </button>
        <button
          onClick={onViewPaymentHistory}
          className="flex items-center justify-center gap-1.5 sm:gap-2 w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-black text-black rounded-lg hover:bg-gray-100 transition-colors text-xs sm:text-sm font-medium"
        >
          <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
          <span className="truncate">Payment History</span>
        </button>
      </div>
    </div>
  );
};

export default AdminPaymentSummary;

